// Canvas toolbar: select / tensator / measure / calibrate tools, layer toggles,
// zoom controls. Reflects the current session mode + layers back into the UI.
import { session, setMode, toggleLayer, type EditorMode, type LayerKey } from "../session";
import { commitTensatorChain } from "../shapes";
import { fitView, zoomByButton } from "../view";

interface ToolbarEls {
  viewSeg: HTMLElement;
  tensatorDone: HTMLButtonElement;
  layerToggles: HTMLElement;
  modeHint: HTMLElement;
  zoomIn: HTMLButtonElement;
  zoomOut: HTMLButtonElement;
  fit: HTMLButtonElement;
  viewport: HTMLElement;
}

let els: ToolbarEls;

const MODE_HINT: Partial<Record<EditorMode, string>> = {
  measure: "Zwei Punkte klicken, um die Distanz zu messen.",
  calibrate: "Zwei Punkte mit bekanntem realem Abstand klicken.",
  shape: "Eckpunkte klicken · Doppelklick/Enter beendet · Esc bricht ab.",
  place: "Auf den Plan klicken, um das Objekt zu platzieren.",
  tensator:
    "Klick für Klick Pfosten setzen – Band entsteht automatisch. »Fertig« beginnt eine neue Absperrung, Esc beendet das Werkzeug.",
};

const LAYER_LABEL: Record<LayerKey, string> = {
  objekte: "Objekte",
  flaechen: "Flächen",
  tensatoren: "Tensatoren",
};

export function initToolbar(elements: ToolbarEls): void {
  els = elements;

  els.viewSeg.querySelectorAll("button").forEach((b) => {
    b.addEventListener("click", () => {
      const mode = b.dataset.mode as EditorMode;
      if (mode !== session().mode) setMode(mode);
    });
  });
  els.tensatorDone.addEventListener("click", () => commitTensatorChain());
  els.zoomIn.addEventListener("click", () => zoomByButton(1.25));
  els.zoomOut.addEventListener("click", () => zoomByButton(0.8));
  els.fit.addEventListener("click", () => fitView());

  els.layerToggles.replaceChildren();
  (Object.keys(LAYER_LABEL) as LayerKey[]).forEach((key) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "layer-toggle";
    b.dataset.layer = key;
    b.textContent = LAYER_LABEL[key];
    b.title = `${LAYER_LABEL[key]} ein-/ausblenden`;
    b.addEventListener("click", () => toggleLayer(key));
    els.layerToggles.appendChild(b);
  });
}

export function renderToolbar(): void {
  const s = session();
  els.viewSeg.querySelectorAll("button").forEach((b) => {
    b.classList.toggle("active", b.dataset.mode === s.mode);
  });
  els.tensatorDone.hidden = s.mode !== "tensator";

  els.layerToggles.querySelectorAll<HTMLElement>(".layer-toggle").forEach((b) => {
    const on = s.layers[b.dataset.layer as LayerKey];
    b.classList.toggle("off", !on);
    b.setAttribute("aria-pressed", String(on));
  });

  const hint = MODE_HINT[s.mode];
  els.modeHint.textContent = hint ?? "";
  els.modeHint.classList.toggle("show", !!hint && s.mode !== "select");

  els.viewport.classList.toggle("mode-place", s.mode === "place");
  els.viewport.classList.toggle("mode-zone", s.mode === "shape" || s.mode === "tensator");
  els.viewport.classList.toggle(
    "mode-measure",
    s.mode === "measure" || s.mode === "calibrate"
  );
}
